import { useState, useRef, useCallback } from "react"
import { useDispatch } from "react-redux"
import { useUploadChunkMutation } from "../slice/aiInterviewApiSlice"
import { setChunkData } from "../slice/aiInterviewSlice"

const UPLOAD_INTERVAL_MS = 5000 // 5 seconds - for ~1MB chunks at 1.6Mbps

export const useRecording = () => {
    const dispatch = useDispatch()
    const [uploadChunk] = useUploadChunkMutation()

    const [isRecording, setIsRecording] = useState(false)
    const [recordingUrl, setRecordingUrl] = useState<string | null>(null)

    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const recordedChunksRef = useRef<Blob[]>([])
    const displayStreamRef = useRef<MediaStream | null>(null)
    const micStreamRef = useRef<MediaStream | null>(null)
    const audioContextRef = useRef<AudioContext | null>(null)
    const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null)
    const micSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null)
    const micGainNodeRef = useRef<GainNode | null>(null)
    const chunkIndexRef = useRef<number>(1)
    const uploadBufferRef = useRef<Blob[]>([])
    const uploadTimerRef = useRef<NodeJS.Timeout | null>(null)
    const isUploadTimerActiveRef = useRef<boolean>(false)
    const isUploadingRef = useRef<boolean>(false)

    const sendBufferedChunk = useCallback(async () => {
        if (isUploadingRef.current) {
            console.log("⏳ Upload already in progress, skipping...")
            return
        }

        if (uploadBufferRef.current.length === 0) {
            return
        }

        try {
            isUploadingRef.current = true

            const bufferToSend = [...uploadBufferRef.current]
            const currentChunkIndex = chunkIndexRef.current

            uploadBufferRef.current = []
            chunkIndexRef.current++

            const combinedBlob = new Blob(bufferToSend, { type: "video/mp4" })

            const invitationId = localStorage.getItem("invitationId")
            if (!invitationId) {
                console.error("❌ No invitation ID found")
                return
            }

            const chunkData = {
                chunkIndex: currentChunkIndex,
                chunk: combinedBlob,
                interviewInvitationId: invitationId,
            }

            const chunkSizeMB = (combinedBlob.size / (1024 * 1024)).toFixed(2)
            console.log(`📤 Uploading chunk ${chunkData.chunkIndex} | Size: ${chunkSizeMB}MB (${combinedBlob.size} bytes)`)

            await uploadChunk(chunkData).unwrap()

            console.log("✅ Chunk uploaded successfully:", chunkData.chunkIndex)

            dispatch(
                setChunkData({
                    chunkIndex: chunkData.chunkIndex,
                    chunk: combinedBlob,
                }),
            )
        } catch (error) {
            console.error("❌ Error sending recording chunk:", error)
        } finally {
            isUploadingRef.current = false
        }
    }, [uploadChunk, dispatch])

    const startUploadTimer = useCallback(() => {
        if (isUploadTimerActiveRef.current) {
            return
        }

        if (uploadTimerRef.current) {
            clearInterval(uploadTimerRef.current)
            uploadTimerRef.current = null
        }

        uploadTimerRef.current = setInterval(() => {
            sendBufferedChunk()
        }, UPLOAD_INTERVAL_MS)

        isUploadTimerActiveRef.current = true
    }, [sendBufferedChunk])

    const stopUploadTimer = useCallback(() => {
        if (uploadTimerRef.current) {
            clearInterval(uploadTimerRef.current)
            uploadTimerRef.current = null
            isUploadTimerActiveRef.current = false
        } else {
            isUploadTimerActiveRef.current = false
        }
    }, [])

    const startSimpleRecording = useCallback(async () => {
        try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
                return
            }

            const ds: MediaStream =
                (window as any).__cqDisplayStream ||
                (await (navigator.mediaDevices as any).getDisplayMedia({
                    video: {
                        width: { ideal: 640, max: 640 },
                        height: { ideal: 360, max: 360 },
                        frameRate: { ideal: 24, max: 24 }
                    },
                    audio: true,
                    preferCurrentTab: true,
                }))
            ;(window as any).__cqDisplayStream = ds
            displayStreamRef.current = ds

            const mic = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: { ideal: true, exact: true },
                    noiseSuppression: { ideal: true, exact: true },
                    autoGainControl: { ideal: true, exact: true },
                    sampleRate: { ideal: 48000 },
                    channelCount: { ideal: 1 },
                },
                video: false,
            })
            micStreamRef.current = mic

            const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
            audioContextRef.current = audioCtx
            const destination = audioCtx.createMediaStreamDestination()
            destinationRef.current = destination

            if (ds.getAudioTracks().length > 0) {
                const tabSource = audioCtx.createMediaStreamSource(ds)
                tabSource.connect(destination)
            }

            const micSource = audioCtx.createMediaStreamSource(mic)
            const micGain = audioCtx.createGain()
            micGain.gain.value = 1.0

            micSource.connect(micGain)
            micGain.connect(destination)

            micSourceNodeRef.current = micSource
            micGainNodeRef.current = micGain

            const videoTrack = ds.getVideoTracks()[0]
            const mixedAudioTrack = destination.stream.getAudioTracks()[0]
            const finalStream = new MediaStream([videoTrack, mixedAudioTrack])

            const mime = MediaRecorder.isTypeSupported("video/mp4")
                ? "video/mp4"
                : MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
                    ? "video/webm;codecs=vp9,opus"
                    : MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
                        ? "video/webm;codecs=vp8,opus"
                        : "video/webm"
            recordedChunksRef.current = []
            chunkIndexRef.current = 1
            uploadBufferRef.current = []

            const rec = new MediaRecorder(finalStream, {
                mimeType: mime,
                videoBitsPerSecond: 1400000,
                audioBitsPerSecond: 128000
            })
            mediaRecorderRef.current = rec

            rec.ondataavailable = (e) => {
                if (e.data && e.data.size) {
                    recordedChunksRef.current.push(e.data)
                    uploadBufferRef.current.push(e.data)
                }
            }

            rec.onstop = async () => {
                try {
                    setIsRecording(false)
                    stopUploadTimer()
                    await sendBufferedChunk()

                    if (!recordingUrl && recordedChunksRef.current.length > 0) {
                        const blob = new Blob(recordedChunksRef.current, { type: mime })
                        const url = URL.createObjectURL(blob)
                        setRecordingUrl(url)
                    }
                } catch { }
            }

            rec.start(1000)
            setIsRecording(true)
            startUploadTimer()
        } catch (error) {
            setIsRecording(false)
        }
    }, [sendBufferedChunk, startUploadTimer, stopUploadTimer, recordingUrl])

    const stopRecording = useCallback(async () => {
        try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
                mediaRecorderRef.current.stop()
            }
        } catch (error) {
            console.error("Error stopping recorder:", error)
        }

        stopUploadTimer()
        await sendBufferedChunk()
    }, [stopUploadTimer, sendBufferedChunk])

    const waitForRecordingFinalize = useCallback(async (maxMs = 7000) => {
        const start = Date.now()
        while (Date.now() - start < maxMs) {
            const rec = mediaRecorderRef.current
            const stopped = !rec || rec.state !== "recording"
            const ready = !!recordingUrl || (recordedChunksRef.current && recordedChunksRef.current.length > 0)
            if (stopped && ready) return
            await new Promise((r) => setTimeout(r, 120))
        }
    }, [recordingUrl])

    const getFinalRecordingUrl = useCallback((): string | null => {
        if (recordingUrl) {
            return recordingUrl
        }

        if (recordedChunksRef.current && recordedChunksRef.current.length > 0) {
            try {
                const blob = new Blob(recordedChunksRef.current, { type: "video/mp4" })
                const url = URL.createObjectURL(blob)
                setRecordingUrl(url)
                return url
            } catch (error) {
                console.error("Error creating recording URL:", error)
            }
        }

        return null
    }, [recordingUrl])

    const muteMicInRecording = useCallback(() => {
        if (micGainNodeRef.current && audioContextRef.current) {
            try {
                micGainNodeRef.current.gain.setValueAtTime(0, audioContextRef.current.currentTime)
                console.log("🔇 Muted mic in recording")
            } catch (error) {
                console.error("Error muting mic:", error)
            }
        }
    }, [])

    const unmuteMicInRecording = useCallback(() => {
        if (micGainNodeRef.current && audioContextRef.current) {
            try {
                micGainNodeRef.current.gain.setValueAtTime(1.0, audioContextRef.current.currentTime)
                console.log("🎤 Unmuted mic in recording")
            } catch (error) {
                console.error("Error unmuting mic:", error)
            }
        }
    }, [])

    return {
        isRecording,
        recordingUrl,
        displayStreamRef,
        micStreamRef,
        audioContextRef,
        destinationRef,
        micSourceNodeRef,
        micGainNodeRef,
        startSimpleRecording,
        stopRecording,
        waitForRecordingFinalize,
        getFinalRecordingUrl,
        muteMicInRecording,
        unmuteMicInRecording,
        stopUploadTimer,
        sendBufferedChunk,
    }
}

